import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import { MessageCompositionFooter } from "../../../../../components/MessageCompositionFooter";
import { buildHtmlMessageWithFiles } from "../../../utils";
import { usePollForInboxThread } from "../../../hooks";
import { toast } from "sonner";

export interface InboxZendeskCompositionFooterProps {
  enduserId?: string;
  subject?: string;
  brandId?: number;
  ccRecipients?: { email: string; name?: string }[];
  isPublic?: boolean;
  onSend?: (result: { success: boolean; ticketId?: number }) => void;
  onPollExhausted?: () => void;
}

export const InboxZendeskCompositionFooter = ({
  enduserId,
  subject,
  brandId,
  ccRecipients,
  isPublic = true,
  onSend,
  onPollExhausted,
}: InboxZendeskCompositionFooterProps) => {
  const { t } = useTranslation("inbox");
  const pollForThread = usePollForInboxThread({
    onMaxAttemptsReached: onPollExhausted,
  });

  const sendMessageMutation = useTellescopeMutation({
    model: "ticket_threads",
    method: "send_message",
    onSuccess: (result) => {
      onSend?.(result);
      if (subject) pollForThread({ type: "Zendesk", title: subject });
    },
    onError: (err) => {
      console.error("Error creating Zendesk ticket: " + JSON.stringify(err, null, 2));
      toast.error(
        t(
          "Something went wrong creating the Zendesk ticket. Please check that you have all necessary permissions in Zendesk before trying again."
        )
      );
    },
  });

  const canSend = !!enduserId && !!subject && !sendMessageMutation.isPending;

  return (
    <MessageCompositionFooter
      editorVariant="richtext"
      channelType="zendesk"
      placeholder={t("Write your message...")}
      canSend={canSend}
      enduserId={enduserId}
      onSubmit={({ message, files }) => {
        if (!enduserId || !subject) return;
        pendo.track("new-zendesk-ticket-send");
        sendMessageMutation.mutate({
          enduserId,
          html_body: buildHtmlMessageWithFiles(message, files),
          public: isPublic,
          subject,
          ...(brandId !== null && { brandId }),
          ...((ccRecipients?.length ?? 0) > 0 && { emailCCs: ccRecipients }),
        });
      }}
    />
  );
};
